import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { signIn } from '@mono/auth-react';
import { FiCheckCircle } from 'react-icons/fi';
import { SiGithub, SiGoogle } from 'react-icons/si';

export const LoginPage = () => {
  const providers = [
    {
      id: 'google',
      label: 'Continue with Google',
      icon: <SiGoogle />,
    },
    {
      id: 'github',
      label: 'Continue with GitHub',
      icon: <SiGithub />,
    },
  ] as const;

  const featurePoints = [
    {
      title: 'Design-to-code sync',
      description:
        'Keep your component library in lock-step across product, design, and engineering.',
    },
    {
      title: 'Accessible out of the box',
      description:
        'Ship inclusive experiences with components that respect WCAG guidelines by default.',
    },
    {
      title: 'Unified workflow',
      description:
        'Automate reviews, deployments, and documentation so teams focus on customer value.',
    },
  ];

  return (
    <Flex minH="100vh" direction={{ base: 'column', lg: 'row' }} bg="bg.canvas">
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
        bg="linear-gradient(140deg, rgba(15,23,42,1) 0%, rgba(30,41,59,0.95) 45%, #000 100%)"
        color="whiteAlpha.900"
        display={{ base: 'none', lg: 'block' }}
      >
        <Flex
          direction="column"
          justify="center"
          minH="100%"
          px={{ base: 6, md: 12, lg: 20 }}
          py={{ base: 12, md: 16, lg: 24 }}
        >
          <Stack
            gap={{ base: 8, md: 10 }}
            maxW={{ base: 'lg', lg: '2xl' }}
            textAlign="left"
            align="flex-start"
          >
            <Box>
              <Badge
                bg="rgba(59,130,246,0.2)"
                color="blue.200"
                borderRadius="full"
                px="3"
                py="1"
                textTransform="uppercase"
                fontWeight="semibold"
                letterSpacing="widest"
              >
                Platform preview
              </Badge>
              <Heading
                size={{ base: '2xl', md: '3xl' }}
                mt="4"
                lineHeight="1.1"
              >
                Design, develop, and ship unified experiences together.
              </Heading>
            </Box>
            <Text
              color="whiteAlpha.700"
              fontSize={{ base: 'md', md: 'xl' }}
              maxW={{ base: 'sm', md: 'lg' }}
            >
              Repacked gives modern teams a single source of truth for their UI,
              documentation, and workflows—so product ideas move from discovery
              to delivery without friction.
            </Text>
            <Stack
              gap="4"
              w="full"
              maxW={{ base: 'sm', md: 'lg' }}
              fontSize={{ base: 'md', md: 'lg' }}
            >
              {featurePoints.map((feature) => (
                <Stack
                  key={feature.title}
                  direction="row"
                  gap="3"
                  align="flex-start"
                >
                  <Icon
                    as={FiCheckCircle}
                    mt="1"
                    boxSize="5"
                    color="blue.200"
                    flexShrink={0}
                  />
                  <Stack gap="1" textAlign={{ base: 'left', lg: 'left' }}>
                    <Text fontWeight="semibold">{feature.title}</Text>
                    <Text color="whiteAlpha.700" fontSize="md">
                      {feature.description}
                    </Text>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Flex>
      </Box>

      <Box
        flex={{ base: 'none', lg: '0 0 420px' }}
        w="full"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bg="bg.surface"
        px={{ base: 4, sm: 6, md: 10 }}
        py={{ base: 12, md: 16, lg: 24 }}
        zIndex={1}
        minH={{ base: '100vh', lg: 'auto' }}
      >
        <Box w="full" maxW={{ base: 'sm', md: 'md', lg: 'sm' }}>
          <Stack gap="6">
            <Box>
              <Heading size="lg">Sign in to continue</Heading>
              <Text color="fg.muted" mt="2">
                Choose your preferred provider to authenticate securely.
              </Text>
            </Box>
            <Stack gap="4">
              {providers.map((provider) => (
                <Button
                  key={provider.id}
                  variant="solid"
                  colorPalette="primary"
                  justifyContent={{ base: 'center', sm: 'flex-start' }}
                  gap="3"
                  size="lg"
                  w="full"
                  onClick={() => signIn(provider.id)}
                >
                  {provider.icon}
                  {provider.label}
                </Button>
              ))}
            </Stack>
            <Text fontSize="sm" color="fg.muted" textAlign="center">
              By signing in you agree to our{' '}
              <Link href="/terms" fontWeight="medium" color="primary.500">
                Terms
              </Link>{' '}
              and{' '}
              <Link href="/privacy" fontWeight="medium" color="primary.500">
                Privacy Policy
              </Link>
              .
            </Text>
          </Stack>
        </Box>
      </Box>
    </Flex>
  );
};
